import JsSIP from "jssip";
import { SoftphoneError, ErrorTypes, ErrorCodes } from "./softphoneErrors";
import EventEmitter from "events";
import { RTCSession } from "jssip/lib/RTCSession";

JsSIP.debug.enable("JsSIP:*");

class SoftphoneServiceClass {
  private userAgent: JsSIP.UA | null = null;
  public currentSession: RTCSession | null = null;
  private eventEmitter = new EventEmitter();
  private config: any;
  private localAudioStream: MediaStream | null = null;
  private remoteAudioStream: MediaStream | null = null;
  private turnCredentials: RTCIceServer | null = null;

  constructor(config: any) {
    // Use a proper type for config
    this.config = config;
    // ... the rest of your initialization
  }
  public getUsername(): string {
    return this.config.sipUsername;
  }

  // Error handling
  private emitError(ErrorTypes: string, code: number, message: string) {
    this.eventEmitter.emit(
      "error",
      new SoftphoneError(ErrorTypes, code, message)
    );
  }

  // Get audio input devices
  public async getAudioInputDevices(): Promise<MediaDeviceInfo[]> {
    try {
      // Request microphone permission first to get device labels
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter((device) => device.kind === "audioinput");
    } catch (error) {
      console.error("Error getting audio devices:", error);
      // If permission denied, still return devices but without labels
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter((device) => device.kind === "audioinput");
    }
  }

  // Get or initialize the local audio stream
  async getLocalAudioStream(): Promise<MediaStream | null> {
    if (!this.localAudioStream) {
      try {
        this.localAudioStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
      } catch (error) {
        console.error(`Error obtaining local stream:`, error);
        this.emitError(
          ErrorTypes.MediaError,
          ErrorCodes.LocalMediaStreamNotPresent.code,
          ErrorCodes.LocalMediaStreamNotPresent.message
        );
        return null;
      }
    }
    return this.localAudioStream;
  }

  // Add local stream tracks to the peer connection
  async handleLocalMediaStream(peerConnection: RTCPeerConnection) {
    const localStream = await this.getLocalAudioStream();
    if (localStream) {
      console.log(
        `[${new Date().toISOString()}] Available local tracks:`,
        localStream.getTracks()
      );
      localStream.getTracks().forEach((track) => {
        console.log(
          `[${new Date().toISOString()}] Adding local track to peer connection:`,
          track
        );
        peerConnection.addTrack(track, localStream);
      });
    } else {
      console.error(
        `[${new Date().toISOString()}] Local stream is null. Cannot add tracks to the peer connection.`
      );
    }
  }

  private async fetchTurnCredentials(): Promise<void> {
    try {
      const res = await fetch('/api/turn/credentials');
      if (res.ok) {
        this.turnCredentials = await res.json();
      }
    } catch {
      // TURN unavailable — fall back to STUN only
    }
  }

  private getIceServers(): RTCIceServer[] {
    const servers: RTCIceServer[] = [{ urls: this.config.sipStunServer }];
    if (this.turnCredentials) servers.push(this.turnCredentials);
    return servers;
  }

  // Initialize and register the user agent
  public async initUserAgent(): Promise<void> {
    if (this.userAgent && this.userAgent.isRegistered()) {
      console.log(`[${new Date().toISOString()}] User agent already registered`);
      this.eventEmitter.emit("registered");
      return;
    }
    console.log(
      `Connecting User Agent ${this.config.sipFullname} to ${this.config.sipServer}...`
    );

    await this.fetchTurnCredentials();

    return new Promise((resolve, reject) => {
      // Set 8 second timeout for connection
      const connectionTimeout = setTimeout(() => {
        console.error(`[${new Date().toISOString()}] Connection timeout after 8 seconds`);
        this.emitError(
          ErrorTypes.RegistrationError,
          ErrorCodes.RegistrationFailed.code,
          "Connection timeout - failed to register within 8 seconds"
        );
        this.eventEmitter.emit("connectionFailed");
        if (this.userAgent) {
          this.userAgent.stop();
          this.userAgent = null;
        }
        reject(new Error("Connection timeout"));
      }, 8000);

      try {
        if (
          !this.config.sipServer ||
          !this.config.sipPort ||
          !this.config.sipPath ||
          !this.config.sipUsername ||
          !this.config.sipPassword
        ) {
          clearTimeout(connectionTimeout);
          this.emitError(
            ErrorTypes.ConfigurationError,
            ErrorCodes.MissingConfiguration.code,
            ErrorCodes.MissingConfiguration.message
          );
          this.eventEmitter.emit("connectionFailed");
          reject(new Error("Missing configuration"));
          return;
        }

        const socketUrl = `wss://${this.config.sipServer}:${this.config.sipPort}${this.config.sipPath}`;
        console.log(`[${new Date().toISOString()}] Attempting WebSocket connection to: ${socketUrl}`);
        const socket = new JsSIP.WebSocketInterface(socketUrl);
        const UAConfig = {
          sockets: [socket],
          uri: `sip:${this.config.sipUsername}@${this.config.sipDomain}`,
          password: this.config.sipPassword,
          display_name: this.config.sipFullname,
          register_expires: 60,
          session_timers: false,
        };
        this.userAgent = new JsSIP.UA(UAConfig);

        this.userAgent.on("newRTCSession", (data: any) => {
          this.handleRTCSession(data);
        });

        // Handle successful WebSocket connection
        this.userAgent.on("connected", () => {
          console.log(
            `[${new Date().toISOString()}] WebSocket connected to ${this.config.sipServer}`
          );
        });

        // Handle successful SIP registration
        this.userAgent.on("registered", () => {
          console.log(
            `[${new Date().toISOString()}] User agent ${this.config.sipFullname} successfully registered to ${this.config.sipServer}`
          );
          clearTimeout(connectionTimeout);
          this.eventEmitter.emit("registered");
          resolve();
        });

        // Handle registration failure
        this.userAgent.on("registrationFailed", (data: any) => {
          console.error(
            `[${new Date().toISOString()}] Registration failed:`,
            data.cause,
            data.response?.status_code
          );
          clearTimeout(connectionTimeout);
          this.emitError(
            ErrorTypes.RegistrationError,
            ErrorCodes.RegistrationFailed.code,
            `Registration failed: ${data.cause}`
          );
          this.eventEmitter.emit("connectionFailed");
          reject(new Error(`Registration failed: ${data.cause}`));
        });

        // Handle WebSocket disconnection
        this.userAgent.on("disconnected", () => {
          console.log(
            `[${new Date().toISOString()}] User agent disconnected from ${this.config.sipServer}`
          );
          clearTimeout(connectionTimeout);
          this.eventEmitter.emit("disconnected");
        });

        this.userAgent.start();
      } catch (error) {
        console.error(`[${new Date().toISOString()}] Error initializing user agent:`, error);
        this.emitError(
          ErrorTypes.RegistrationError,
          ErrorCodes.RegistrationFailed.code,
          ErrorCodes.RegistrationFailed.message
        );
        this.eventEmitter.emit("connectionFailed");
        reject(error);
      }
    });
  }

  // Event handlers for the RTC session
  private handleRTCSession(data: any) {
    const session: RTCSession = data.session;
    this.currentSession = session;
    console.log(
      `[${new Date().toISOString()}] New RTC session (direction: ${session.direction})`
    );

    session.on("peerconnection", async (event: any) => {
      console.log(
        `[${new Date().toISOString()}] Peerconnection event detected`
      );
      const peerConnection = event.peerconnection;

      // Call handleLocalStream right after the 'peerconnection' event to ensure local tracks are added
      this.handleLocalMediaStream(peerConnection);

      peerConnection.ontrack = (trackEvent) => {
        console.log(`[${new Date().toISOString()}] ontrack event received.`);
        if (trackEvent.streams && trackEvent.streams[0]) {
          const remoteStream = trackEvent.streams[0];
          console.log(
            `[${new Date().toISOString()}] Available remote tracks:`,
            remoteStream.getTracks()
          );
          this.remoteAudioStream = remoteStream;
          console.log(
            `[${new Date().toISOString()}] Remote stream obtained:`,
            this.remoteAudioStream
          );
          this.remoteAudioStream?.getTracks().forEach((track) => {
            console.log(
              `[${new Date().toISOString()}] Track added to remote stream:`,
              track
            );
          });
          this.eventEmitter.emit(
            "remoteStreamObtained",
            this.remoteAudioStream
          );
        } else {
          console.log(
            `[${new Date().toISOString()}] No stream associated with the ontrack event.`
          );
        }
      };
      //   this.setupPeerConnectionEvents(peerConnection);
    });

    // Event for when the call starts connecting
    session.on("connecting", () => {
      console.log(`[${new Date().toISOString()}] Call is connecting...`);
      this.eventEmitter.emit("callConnecting");
    });

    // Event for call in progress (e.g., Dialing or Incoming Call)
    session.on("progress", (e) => {
      if (e.originator === "remote" && e.response) {
        console.log(
          `[${new Date().toISOString()}] Received provisional response with SDP answer:`,
          e.response.body
        );
      }
      // if call direction incoming
      if (session.direction === "incoming") {
        console.log(`[${new Date().toISOString()}] Incoming call detected...`);
        this.eventEmitter.emit("incomingCall");
      }
    });

    // Call ended event
    session.on("ended", () => {
      console.log(`[${new Date().toISOString()}] Call ended.`);
      this.eventEmitter.emit("callEnded");
      this.currentSession = null;
    });

    // Call failed event
    session.on("failed", () => {
      console.log(`[${new Date().toISOString()}] Call failed.`);
      this.eventEmitter.emit("callFailed");
      this.currentSession = null;
    });

    // Only for the callee when the call is accepted
    session.on("accepted", async () => {
      console.log(`[${new Date().toISOString()}] Call accepted.`);
    });

    // Call confirmed event
    session.on("confirmed", () => {
      console.log(`[${new Date().toISOString()}] Call confirmed.`);
      this.eventEmitter.emit("callActive");
    });

    session.on("sdp", async (event) => {
      // Log the type of SDP (offer/answer) and the content
      console.log(
        `[${new Date().toISOString()}] SDP type: ${event.originator} - ${event.type}`
      );
      console.log(event.sdp);
    });
  }

  public async makeCall(phoneNumber: string): Promise<void> {
    try {
      console.log(`makeCall invoked with phoneNumber: ${phoneNumber}`); // Initial log

      if (!this.userAgent || !this.userAgent.isRegistered()) {
        this.emitError(
          ErrorTypes.ConnectionError,
          ErrorCodes.ConnectionFailed.code,
          ErrorCodes.ConnectionFailed.message
        );
        return;
      }

      if (!phoneNumber.trim()) {
        this.eventEmitter.emit("callFailed");
        this.emitError(
          ErrorTypes.CallError,
          ErrorCodes.NumberInvalid.code,
          ErrorCodes.NumberInvalid.message
        );
        return;
      }

      // Check if the call is internal (3-digit extension)
      const isInternalCall = /^\d{3}$/.test(phoneNumber);

      // Format the phone number for outbound calls
      if (!isInternalCall) {
        // Ensure the number has the correct format for Telnyx
        if (phoneNumber.length === 10) {
          phoneNumber = `1${phoneNumber}`; // Format to E.164 if it's a 10-digit US number
        }
        console.log(`Formatted number for outbound call: ${phoneNumber}`);
      }

      // ALL calls go through Asterisk (sipDomain), Asterisk handles routing to Telnyx trunk
      const sipUri = `sip:${phoneNumber}@${this.config.sipDomain}`;
      console.log(`SIP URI for the call: ${sipUri}`); // Log final SIP URI

      // Options for the JsSIP call
      const options = {
        mediaConstraints: { audio: true, video: false }, // Video can be added if needed
        eventHandlers: {
          peerconnection: (e: any) => {
            console.log(
              `[${new Date().toISOString()}] Peerconnection event detected`
            );
            const peerConnection = e.peerconnection;

            // Force ICE gathering complete 500ms after first relay candidate.
            // JsSIP 3.x has no gatheringTimeout — without this Chrome iterates
            // all NICs (Tailscale, VirtualBox, WSL, etc.) and waits ~40s for
            // each to time out before declaring gathering complete.
            let gatheringTimer: ReturnType<typeof setTimeout> | null = null;
            peerConnection.addEventListener('icecandidate', (evt: RTCPeerConnectionIceEvent) => {
              if (evt.candidate?.type === 'relay' && !gatheringTimer) {
                gatheringTimer = setTimeout(() => {
                  if (peerConnection.iceGatheringState !== 'complete') {
                    Object.defineProperty(peerConnection, 'iceGatheringState', {
                      get: () => 'complete' as RTCIceGatheringState,
                      configurable: true,
                    });
                    peerConnection.dispatchEvent(new Event('icegatheringstatechange'));
                    console.log(`[${new Date().toISOString()}] ICE gathering force-completed after relay candidate`);
                  }
                }, 500);
              }
            });

            // Log ICE connection state changes
            peerConnection.oniceconnectionstatechange = () => {
              console.log(
                `[${new Date().toISOString()}] ICE connection state: ${peerConnection.iceConnectionState}`
              );
            };

            // Log ICE gathering state changes
            peerConnection.onicegatheringstatechange = () => {
              console.log(
                `[${new Date().toISOString()}] ICE gathering state: ${peerConnection.iceGatheringState}`
              );
            };

            // Log ICE candidates
            peerConnection.onicecandidate = (event) => {
              if (event.candidate) {
                console.log(
                  `[${new Date().toISOString()}] ICE candidate:`,
                  event.candidate.candidate
                );
              } else {
                console.log(
                  `[${new Date().toISOString()}] ICE gathering completed`
                );
              }
            };

            peerConnection.ontrack = (event) => {
              console.log(
                `[${new Date().toISOString()}] Received remote track:`,
                event.track
              );
              if (event.streams && event.streams[0]) {
                this.remoteAudioStream = event.streams[0];
                this.eventEmitter.emit(
                  "remoteStreamObtained",
                  this.remoteAudioStream
                );
              }
            };
          },
          failed: (e: any) => {
            console.error(
              `[${new Date().toISOString()}] Call failed with cause: ${e.cause}`
            );
            this.emitError(
              ErrorTypes.CallError,
              ErrorCodes.UserAgentUnavailable.code,
              ErrorCodes.UserAgentUnavailable.message
            );
          },
          ended: (e: any) => {
            console.log(
              `[${new Date().toISOString()}] Call ended with cause: ${e.cause}`
            );
          },
          confirmed: (e: any) => {
            console.log(`[${new Date().toISOString()}] Call confirmed`);
          },
        },
        pcConfig: {
          iceServers: this.getIceServers(),
          iceTransportPolicy: 'relay',
        },
      };

      // Make the call
      this.currentSession = this.userAgent.call(sipUri, options);
    } catch (error) {
      console.error(
        `[${new Date().toISOString()}] Error making call: ${error}`
      );
      this.emitError(
        ErrorTypes.CallError,
        ErrorCodes.CallFailed.code,
        ErrorCodes.CallFailed.message
      );
    }
  }

  public async answerCall(): Promise<void> {
    if (!this.currentSession) {
      this.emitError(
        ErrorTypes.CallError,
        ErrorCodes.NoSessionToAnswer.code,
        ErrorCodes.NoSessionToAnswer.message
      );
      return;
    }
    try {
      const options = {
        mediaConstraints: { audio: true, video: false },
        pcConfig: {
          iceServers: this.getIceServers(),
          iceTransportPolicy: 'relay',
        },
      };
      this.currentSession.answer(options);
      // Ensure local stream is handled in the 'peerconnection' event of the session
    } catch (error) {
      this.emitError(
        ErrorTypes.CallError,
        ErrorCodes.CallFailed.code,
        ErrorCodes.CallFailed.message
      );
    }
  }

  public endCall(): void {
    if (this.currentSession) {
      // Remove tracks from the peer connection
      const senders = this.currentSession.connection?.getSenders();
      senders?.forEach((sender) =>
        this.currentSession?.connection?.removeTrack(sender)
      );

      // Terminate the session
      this.currentSession.terminate();
      this.currentSession = null;
    }
  }

  public disconnectUserAgent(): void {
    if (this.userAgent) {
      this.userAgent.stop();
      this.userAgent = null;
      console.log(
        `[${new Date().toISOString()}] User agent ${this.config.sipFullname} disconnected.`
      );
      this.eventEmitter.emit("statusChange", "Disconnected");
    }
  }

  // mute local audio and emit muted event
  // Toggle local audio mute state and emit appropriate event
  public toggleMute(): void {
    if (this.localAudioStream) {
      const audioTracks = this.localAudioStream.getAudioTracks();
      const currentlyMuted = audioTracks[0].enabled;
      audioTracks.forEach((track) => {
        track.enabled = !currentlyMuted;
      });
      this.eventEmitter.emit(currentlyMuted ? "audioUnmuted" : "audioMuted");
    }
  }

  public on(event: string, listener: (...args: any[]) => void): void {
    this.eventEmitter.on(event, listener);
  }

  public off(event: string, listener: (...args: any[]) => void): void {
    this.eventEmitter.off(event, listener);
  }
}

export default SoftphoneServiceClass;
